/**
 * Input capture: keyboard, mouse look via pointer lock, and edge detection.
 *
 * Two properties matter for feel:
 *
 *  1. Mouse deltas accumulate between reads rather than being sampled. A 120 Hz
 *     mouse reporting twice in one frame must contribute both movements or aim
 *     feels like it drops input.
 *
 *  2. Presses are edge-latched and consumed by the fixed step, not the render
 *     frame. A jump tapped between two physics ticks must survive to the next
 *     tick — otherwise inputs vanish at high frame rates, which is exactly the
 *     kind of bug that reads as "the controls feel unreliable".
 */

const KEY_MAP = {
  KeyW: 'forward',
  KeyS: 'back',
  KeyA: 'left',
  KeyD: 'right',
  Space: 'jump',
  ShiftLeft: 'sprint',
  ShiftRight: 'sprint',
  ControlLeft: 'crouch',
  ControlRight: 'crouch',
  KeyC: 'crouch',
  KeyR: 'reload',
  KeyQ: 'grenade',
  KeyE: 'melee',
  KeyF: 'super',
  KeyV: 'melee',
  Tab: 'score',
};

export class Input {
  constructor(canvas, events, opts = {}) {
    this.canvas = canvas;
    this.events = events;

    // Mouse input normally requires pointer lock, both because look only works
    // with movementX/Y and because a click that has not captured the pointer
    // should not fire the weapon. The headless capture harness cannot acquire
    // pointer lock at all, so it disables the requirement and injects look
    // directly into the camera rig instead.
    this.requireLock = opts.requireLock !== false;

    /** Held state, keyed by action name. */
    this.down = Object.create(null);
    /** Edge-latched presses, cleared when the fixed step consumes them. */
    this._pressed = Object.create(null);
    this._released = Object.create(null);

    this.mouseDX = 0;
    this.mouseDY = 0;
    this.locked = false;
    this.sensitivity = 0.0022; // radians per pixel at 1.0 multiplier
    this.sensitivityScale = 1;
    this.invertY = false;

    // Movement axes, resolved once per tick.
    this.moveX = 0; // strafe, +right
    this.moveY = 0; // forward, +forward

    this._enabled = true;
    this._bind();
  }

  _bind() {
    const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

    this._onKeyDown = (e) => {
      if (!this._enabled) return;
      const action = KEY_MAP[e.code];
      if (!action) return;
      if (e.repeat) return; // autorepeat must not re-trigger an edge
      if (e.code === 'Tab') stop(e);
      if (e.code === 'Space') stop(e); // otherwise Space scrolls the page
      if (!this.down[action]) this._pressed[action] = true;
      this.down[action] = true;
    };

    this._onKeyUp = (e) => {
      const action = KEY_MAP[e.code];
      if (!action) return;
      this.down[action] = false;
      this._released[action] = true;
    };

    this._onMouseMove = (e) => {
      if ((this.requireLock && !this.locked) || !this._enabled) return;
      // Accumulate; do not overwrite. See note in the class doc.
      this.mouseDX += e.movementX || 0;
      this.mouseDY += e.movementY || 0;
    };

    this._onMouseDown = (e) => {
      if ((this.requireLock && !this.locked) || !this._enabled) return;
      const action = e.button === 0 ? 'fire' : e.button === 2 ? 'ads' : null;
      if (!action) return;
      if (!this.down[action]) this._pressed[action] = true;
      this.down[action] = true;
    };

    this._onMouseUp = (e) => {
      const action = e.button === 0 ? 'fire' : e.button === 2 ? 'ads' : null;
      if (!action) return;
      this.down[action] = false;
      this._released[action] = true;
    };

    this._onContextMenu = (e) => e.preventDefault();

    this._onLockChange = () => {
      this.locked = document.pointerLockElement === this.canvas;
      document.body.classList.toggle('locked', this.locked);
      // Releasing the cursor must clear held state, or the player keeps
      // sprinting forward while the pause menu is open.
      if (!this.locked) this.clearHeld();
      this.events.emit('input:lock', { locked: this.locked });
    };

    window.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('keyup', this._onKeyUp);
    window.addEventListener('mousemove', this._onMouseMove);
    window.addEventListener('mousedown', this._onMouseDown);
    window.addEventListener('mouseup', this._onMouseUp);
    window.addEventListener('contextmenu', this._onContextMenu);
    document.addEventListener('pointerlockchange', this._onLockChange);
    window.addEventListener('blur', () => this.clearHeld());
  }

  requestLock() {
    if (this.locked) return;
    const p = this.canvas.requestPointerLock?.({ unadjustedMovement: true });
    // unadjustedMovement bypasses OS pointer acceleration, which otherwise makes
    // slow aim adjustments non-linear. Not supported everywhere; fall back quietly.
    if (p && typeof p.catch === 'function') {
      p.catch(() => this.canvas.requestPointerLock());
    }
  }

  releaseLock() {
    if (this.locked) document.exitPointerLock();
  }

  setEnabled(on) {
    this._enabled = on;
    if (!on) this.clearHeld();
  }

  clearHeld() {
    for (const k in this.down) this.down[k] = false;
    this.mouseDX = 0;
    this.mouseDY = 0;
  }

  /** True exactly once per press. Consumed on read. */
  pressed(action) {
    if (this._pressed[action]) {
      this._pressed[action] = false;
      return true;
    }
    return false;
  }

  /** Non-consuming peek, for systems that need to observe without claiming. */
  peekPressed(action) {
    return !!this._pressed[action];
  }

  released(action) {
    if (this._released[action]) {
      this._released[action] = false;
      return true;
    }
    return false;
  }

  held(action) {
    return !!this.down[action];
  }

  /** Resolve WASD into normalized axes. Called at the start of each fixed step. */
  updateAxes() {
    let x = (this.down.right ? 1 : 0) - (this.down.left ? 1 : 0);
    let y = (this.down.forward ? 1 : 0) - (this.down.back ? 1 : 0);
    // Normalize so diagonal movement is not 1.41x faster.
    if (x !== 0 && y !== 0) {
      const inv = Math.SQRT1_2;
      x *= inv;
      y *= inv;
    }
    this.moveX = x;
    this.moveY = y;
  }

  /** Drain accumulated look delta in radians. Called once per fixed step. */
  consumeLook(out) {
    const s = this.sensitivity * this.sensitivityScale;
    out.x = this.mouseDX * s;
    out.y = this.mouseDY * s * (this.invertY ? -1 : 1);
    this.mouseDX = 0;
    this.mouseDY = 0;
    return out;
  }

  /** Clear one-shot edges. Called at the very end of the fixed step. */
  endStep() {
    for (const k in this._pressed) this._pressed[k] = false;
    for (const k in this._released) this._released[k] = false;
  }

  dispose() {
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    window.removeEventListener('mousemove', this._onMouseMove);
    window.removeEventListener('mousedown', this._onMouseDown);
    window.removeEventListener('mouseup', this._onMouseUp);
    window.removeEventListener('contextmenu', this._onContextMenu);
    document.removeEventListener('pointerlockchange', this._onLockChange);
  }
}
